import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import { useNotificationStore } from '@/stores/notification.store';
import { useAuthStore } from '@/stores/auth.store';
import type { Notification, PaginatedResponse } from '@/types/api.types';

const RECONNECT_DELAY_MS = 5000;

export function useNotifications() {
  const { accessToken: token } = useAuthStore();
  const { setNotifications, addNotification, markRead, markAllRead } = useNotificationStore();
  const qc = useQueryClient();
  const esRef = useRef<EventSource | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial load
  useQuery<PaginatedResponse<Notification>>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await apiClient.get<PaginatedResponse<Notification>>('/api/v1/notifications?limit=50');
      return res.data;
    },
    enabled: !!token,
    select: (data) => {
      setNotifications(data.data);
      return data;
    },
  });

  // SSE connection
  useEffect(() => {
    if (!token) return;

    function connect() {
      const url = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/v1/notifications/stream?token=${encodeURIComponent(token ?? '')}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data as string) as Notification;
          addNotification(data);
        } catch {
          // heartbeat or unknown message — ignore
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        reconnectRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      };
    }

    connect();

    return () => {
      esRef.current?.close();
      esRef.current = null;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [token, addNotification]);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/v1/notifications/${id}/read`, {}),
    onSuccess: (_data, id) => {
      markRead(id);
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () => apiClient.post('/api/v1/notifications/read-all', {}),
    onSuccess: () => {
      markAllRead();
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    markRead: (id: string) => markReadMutation.mutate(id),
    markAll: () => markAllMutation.mutate(),
  };
}
